import { useRef, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, FileText, X } from "lucide-react";

// Shared "Importar lista" dialog: paste names (one per line) and/or upload .txt/.csv/.xlsx; items are created in the given order.
export function ImportDialog({ resource, label, onDone, triggerTestId = "import-btn" }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const submit = async () => {
    const names = text.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
    if (!names.length && !file) return toast.error("Cole os nomes ou selecione um arquivo");
    const count = names.length + (file ? 0 : 0);
    if (!window.confirm(`Importar ${file ? "o arquivo " + file.name + (count ? ` e mais ${count} nome(s)` : "") : count + " nome(s)"} como ${label}? Nomes já existentes serão ignorados.`)) return;
    setBusy(true);
    try {
      let payload = { names };
      if (file) {
        const b64 = await new Promise((res) => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.readAsDataURL(file); });
        payload = { ...payload, file_b64: b64, filename: file.name };
      }
      const r = await api.post(`/${resource}/import`, payload);
      toast.success(`${r.data.created.length} ${label}(s) criado(s)${r.data.skipped.length ? ` · ${r.data.skipped.length} ignorado(s) por já existirem` : ""}`, { duration: 6000 });
      setOpen(false); setText(""); setFile(null); onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid={triggerTestId}><Upload className="w-4 h-4 mr-2" /> Importar lista</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white dark:bg-slate-900 sm:max-w-lg">
          <DialogHeader><DialogTitle>Importar {label}s</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Cole os nomes (um por linha)</Label>
              <Textarea rows={7} value={text} onChange={(e) => setText(e.target.value)} placeholder={"Ex.:\nMatriz\nFilial Centro\nFilial Norte"} data-testid="import-textarea" />
              <p className="text-xs text-slate-500 mt-1">Serão criados na mesma ordem em que aparecem.</p>
            </div>
            <div className="rounded-lg border border-dashed border-slate-300 dark:border-slate-700 p-3">
              <input ref={fileRef} type="file" accept=".txt,.csv,.xlsx" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="import-file-input" />
              {file ? (
                <div className="flex items-center gap-2 text-sm"><FileSpreadsheet className="w-4 h-4 text-emerald-600" /><span className="truncate flex-1">{file.name}</span><button onClick={() => setFile(null)} className="text-slate-400 hover:text-rose-600" data-testid="import-file-clear"><X className="w-4 h-4" /></button></div>
              ) : (
                <button onClick={() => fileRef.current?.click()} className="w-full text-sm text-slate-600 dark:text-slate-300 flex items-center justify-center gap-2 py-2" data-testid="import-file-btn"><FileText className="w-4 h-4" /> Ou envie um arquivo .txt, .csv ou .xlsx (um nome por linha / primeira coluna)</button>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={submit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="import-submit-btn">{busy ? "Importando..." : "Importar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
